import countriesCa from "i18n-iso-countries/langs/ca.json";
import countriesEs from "i18n-iso-countries/langs/es.json";
import countriesEn from "i18n-iso-countries/langs/en.json";

// We read the shipped JSON datasets directly instead of using the
// i18n-iso-countries runtime, which resolves ./langs/*.json dynamically and
// therefore cannot be bundled for the edge runtime.
type CountryData = { countries: Record<string, string | string[]> };

const COUNTRY_NAMES: Record<string, Record<string, string | string[]>> = {
  ca: (countriesCa as unknown as CountryData).countries,
  es: (countriesEs as unknown as CountryData).countries,
  en: (countriesEn as unknown as CountryData).countries,
};

function getCountryName(code: string, lang: string): string | undefined {
  const entry = COUNTRY_NAMES[lang]?.[code];
  if (!entry) return undefined;
  return Array.isArray(entry) ? entry[0] : entry;
}


export function formatCompact(n: number | null | undefined): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
}

/** Locale-aware full number (e.g. 5.786 in Spanish). */
export function formatNumber(n: number | null | undefined, lang?: string): string {
  if (n == null) return "—";
  return new Intl.NumberFormat((lang || "en").slice(0, 2)).format(n);
}

/** Human-readable byte size, e.g. "1,2 MB". */
export function formatBytes(n: number | null | undefined, lang?: string): string {
  if (n == null) return "—";
  if (n >= 1024 * 1024) return formatNumber(n / (1024 * 1024), lang) + " MB";
  return formatNumber(Math.round(n / 1024), lang) + " KB";
}

/**
 * Neteja el títol per mostrar-lo a la UI:
 * - elimina emoticones i banderes (regional indicators)
 * - elimina variation selectors i ZWJ
 * - converteix paraules en MAJÚSCULES a Format Títol (primera lletra en majúscula)
 * - col·lapsa espais i signes de puntuació orfes
 * No modifica el valor guardat a la base de dades.
 */
export function cleanTitle(s?: string | null): string {
  if (!s) return "";
  let out = s
    .replace(/\p{Extended_Pictographic}/gu, "")
    .replace(/\p{Regional_Indicator}/gu, "")
    .replace(/[\uFE0F\u200D]/g, "");
  out = out.replace(/\p{Lu}[\p{Lu}\p{N}'’]+/gu, (w) => w.charAt(0) + w.slice(1).toLowerCase());
  // netegem espais i signes orfes que hagin quedat després d'eliminar emojis/banderes
  out = out.replace(/\s+([,.!?;:])/g, "$1");
  out = out.replace(/\s{2,}/g, " ").trim();
  // treu separadors residuals a inici/final (·, -, |, etc.)
  out = out.replace(/^[\s·\-–—|:,.]+|[\s·\-–—|:,.]+$/g, "").trim();
  return out;
}

export function formatDate(iso: string | null | undefined, lang?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const l = (lang || "en").toLowerCase();
  if (l.startsWith("ca") || l.startsWith("es")) {
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const yyyy = d.getUTCFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }
  return new Intl.DateTimeFormat(l || "en", { dateStyle: "medium", timeZone: "UTC" }).format(d);
}

export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 1) return "today";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export function countryFlag(cc: string | null | undefined): string {
  if (!cc || cc.length !== 2) return "";
  const A = 0x1f1e6;
  const base = "A".charCodeAt(0);
  const chars = cc.toUpperCase().split("").map((c) => A + c.charCodeAt(0) - base);
  return String.fromCodePoint(...chars);
}

/** Reliable flag image URL (SVG). Works on all platforms including Windows. */
export function countryFlagUrl(cc: string | null | undefined, size: 20 | 40 | 80 = 20): string | null {
  if (!cc || cc.length !== 2) return null;
  return `https://flagcdn.com/w${size}/${cc.toLowerCase()}.png`;
}

/** Translated country display name. Falls back to the given name or the code. */
export function translateCountry(
  code: string | null | undefined,
  fallback: string | null | undefined,
  lang: string,
): string {
  // A shipped dataset (instead of Intl.DisplayNames) keeps names identical on
  // the server and in every browser, so server-rendered pages hydrate cleanly.
  if (code && code.length === 2) {
    const l = (lang || "ca").slice(0, 2).toLowerCase();
    const name = getCountryName(code.toUpperCase(), ["ca", "es", "en"].includes(l) ? l : "en");
    if (name) return name;

  }
  return fallback ?? code ?? "—";
}
